/**
 * Coordinate Transformation Utilities for Magic Marker
 *
 * Handles conversion between different coordinate systems:
 * - PDF coordinates (bottom-left origin, points: 1/72 inch)
 * - Screen coordinates (top-left origin, pixels)
 * - Zoomed coordinates (scaled for zoom levels)
 */

export interface Coordinates {
  x: number;
  y: number;
  width: number;
  height: number;
  page: number;
}

export interface PDFDimensions {
  pageWidth: number;    // PDF page width in points
  pageHeight: number;   // PDF page height in points
  scale: number;        // Current zoom/scale factor (1.0 = 100%)
  viewportWidth: number; // Actual rendered width in pixels
  viewportHeight: number; // Actual rendered height in pixels
}

/**
 * Standard PDF page sizes in points (1 point = 1/72 inch)
 */
export const PDF_PAGE_SIZES = {
  A4: { width: 595, height: 842 },
  LETTER: { width: 612, height: 792 },
  LEGAL: { width: 612, height: 1008 },
  A3: { width: 842, height: 1191 },
};

/**
 * Auto-detect coordinate format and convert to normalized (0-1 scale)
 *
 * Handles backward compatibility with absolute PDF point coordinates
 * stored in the database before normalization was implemented.
 *
 * Detection logic:
 * - If any coordinate value > 1.0 → Absolute PDF points (old format)
 * - If all values <= 1.0 → Already normalized (new format)
 *
 * @param coords - Coordinates in either absolute or normalized format
 * @param pageWidth - PDF page width in points (for conversion if needed)
 * @param pageHeight - PDF page height in points (for conversion if needed)
 * @returns Coordinates normalized to 0-1 scale
 *
 * @example
 * // Absolute coordinates (old format)
 * const normalized = convertToNormalized(
 *   { x: 212.09, y: 384.28, width: 46.06, height: 10.07, page: 1 },
 *   595,  // pageWidth
 *   841   // pageHeight
 * );
 * // Returns: { x: 0.3564, y: 0.4566, width: 0.0774, height: 0.0120, page: 1 }
 *
 * // Normalized coordinates (new format) - returned as-is
 * const normalized2 = convertToNormalized(
 *   { x: 0.3564, y: 0.4566, width: 0.0774, height: 0.0120, page: 1 },
 *   595,
 *   841
 * );
 * // Returns: { x: 0.3564, y: 0.4566, width: 0.0774, height: 0.0120, page: 1 } (unchanged)
 */
export function convertToNormalized(
  coords: Coordinates,
  pageWidth: number,
  pageHeight: number
): { coords: Coordinates; format: 'absolute' | 'normalized' } {
  // Validate page dimensions
  if (pageWidth <= 0 || pageHeight <= 0) {
    console.warn('⚠️ Invalid page dimensions:', pageWidth, pageHeight);
    return { coords, format: 'normalized' }; // Return as-is if dimensions invalid
  }

  // Detect format: if any coordinate > 1.0, it's absolute PDF points
  const isAbsolute =
    coords.x > 1.0 ||
    coords.y > 1.0 ||
    coords.width > 1.0 ||
    coords.height > 1.0;

  if (isAbsolute) {
    // Convert absolute PDF points to normalized 0-1 scale
    return {
      coords: {
        x: coords.x / pageWidth,
        y: coords.y / pageHeight,
        width: coords.width / pageWidth,
        height: coords.height / pageHeight,
        page: coords.page
      },
      format: 'absolute'
    };
  } else {
    // Already normalized, return as-is
    return { coords, format: 'normalized' };
  }
}

/**
 * Convert normalized coordinates (0-1 scale) to screen coordinates
 *
 * This is the RECOMMENDED method for coordinate transformation.
 * Normalized coordinates are resolution-independent and work at any zoom level.
 *
 * Normalized coordinates use 0-1 scale where:
 * - x: 0.0 = left edge, 1.0 = right edge
 * - y: 0.0 = bottom edge, 1.0 = top edge (PDF coordinate system)
 * - width: fraction of page width
 * - height: fraction of page height
 *
 * @param normalizedCoords - Coordinates in normalized 0-1 space (from backend OCR)
 * @param renderWidth - Actual rendered width in pixels (from react-pdf PageRenderInfo)
 * @param renderHeight - Actual rendered height in pixels (from react-pdf PageRenderInfo)
 * @returns Coordinates in screen pixel space
 *
 * @example
 * const screenCoords = normalizedToScreen(
 *   { x: 0.168, y: 0.832, width: 0.336, height: 0.036, page: 1 },
 *   800,   // renderWidth from react-pdf
 *   1133   // renderHeight from react-pdf
 * );
 * // Returns: { x: 134, y: 152, width: 269, height: 41, page: 1 }
 */
export function normalizedToScreen(
  normalizedCoords: Coordinates,
  renderWidth: number,
  renderHeight: number
): Coordinates {
  return {
    // X coordinate: scale from 0-1 to screen pixels
    x: normalizedCoords.x * renderWidth,

    // Y coordinate: flip Y axis (PDF origin at bottom-left, screen at top-left) and scale
    // In normalized PDF: y=0 is at BOTTOM, y=1 is at TOP
    // In Screen: y=0 is at TOP, increases DOWNWARD
    y: (1.0 - normalizedCoords.y - normalizedCoords.height) * renderHeight,

    // Width and height: scale from 0-1 to screen pixels
    width: normalizedCoords.width * renderWidth,
    height: normalizedCoords.height * renderHeight,

    page: normalizedCoords.page
  };
}

/**
 * Convert PDF coordinates to screen coordinates
 *
 * PDF uses bottom-left origin in points (1/72 inch)
 * Screen uses top-left origin in pixels
 *
 * This version uses REAL page dimensions from the backend and actual rendering dimensions
 * from react-pdf for pixel-perfect accuracy.
 *
 * @deprecated Use normalizedToScreen() instead for better accuracy and simplicity
 * @param pdfCoords - Coordinates in PDF space (from backend OCR)
 * @param actualPageWidth - Actual PDF page width in points (from backend page_dimensions)
 * @param actualPageHeight - Actual PDF page height in points (from backend page_dimensions)
 * @param renderWidth - Actual rendered width in pixels (from react-pdf PageRenderInfo)
 * @param renderHeight - Actual rendered height in pixels (from react-pdf PageRenderInfo)
 * @returns Coordinates in screen space
 *
 * @example
 * const screenCoords = pdfToScreen(
 *   { x: 100, y: 700, width: 200, height: 30, page: 1 },
 *   595,   // actualPageWidth from backend
 *   842,   // actualPageHeight from backend
 *   800,   // renderWidth from react-pdf
 *   1133   // renderHeight from react-pdf
 * );
 */
export function pdfToScreen(
  pdfCoords: Coordinates,
  actualPageWidth: number,
  actualPageHeight: number,
  renderWidth: number,
  renderHeight: number
): Coordinates {
  // Calculate EXACT scaling factors from PDF points to rendered pixels
  const scaleX = renderWidth / actualPageWidth;
  const scaleY = renderHeight / actualPageHeight;

  return {
    // X coordinate: scale from PDF points to screen pixels
    x: pdfCoords.x * scaleX,

    // Y coordinate: flip Y axis (PDF origin at bottom-left, screen at top-left) and scale
    // In PDF: y=0 is at BOTTOM, increases UPWARD
    // In Screen: y=0 is at TOP, increases DOWNWARD
    y: (actualPageHeight - pdfCoords.y - pdfCoords.height) * scaleY,

    // Width and height: scale from PDF points to screen pixels
    width: pdfCoords.width * scaleX,
    height: pdfCoords.height * scaleY,

    page: pdfCoords.page
  };
}

/**
 * DEPRECATED: Old version using PDFDimensions - kept for backward compatibility
 * Use the new pdfToScreen() with explicit dimensions instead
 */
export function pdfToScreenLegacy(
  pdfCoords: Coordinates,
  pdfDimensions: PDFDimensions,
  pageGap: number = 20
): Coordinates {
  const { pageWidth, pageHeight, scale, viewportWidth } = pdfDimensions;

  // Calculate scaling factor from PDF points to screen pixels
  // If viewport width is provided, use it; otherwise use scale
  const pixelScale = viewportWidth > 0
    ? viewportWidth / pageWidth
    : scale;

  // Calculate page offset for multi-page documents
  // Pages are stacked vertically with gaps between them
  const pageNumber = pdfCoords.page || 1;
  const pageOffset = calculatePageOffset(pageNumber, pageHeight * pixelScale, pageGap);

  return {
    // Flip Y axis: PDF origin at bottom-left, screen at top-left
    x: pdfCoords.x * pixelScale,
    y: (pageHeight - pdfCoords.y - pdfCoords.height) * pixelScale + pageOffset,
    width: pdfCoords.width * pixelScale,
    height: pdfCoords.height * pixelScale,
    page: pdfCoords.page
  };
}

/**
 * Convert screen coordinates to PDF coordinates
 *
 * @param screenCoords - Coordinates in screen space
 * @param pdfDimensions - PDF page dimensions and scale
 * @returns Coordinates in PDF space
 *
 * @example
 * const pdfCoords = screenToPDF(
 *   { x: 100, y: 142, width: 200, height: 30, page: 1 },
 *   { pageWidth: 595, pageHeight: 842, scale: 1, ... }
 * );
 */
export function screenToPDF(
  screenCoords: Coordinates,
  pdfDimensions: PDFDimensions
): Coordinates {
  const { pageWidth, pageHeight, scale, viewportWidth } = pdfDimensions;

  // Calculate scaling factor
  const pixelScale = viewportWidth > 0
    ? viewportWidth / pageWidth
    : scale;

  return {
    x: screenCoords.x / pixelScale,
    // Flip Y axis back to PDF coordinates
    y: pageHeight - (screenCoords.y / pixelScale) - (screenCoords.height / pixelScale),
    width: screenCoords.width / pixelScale,
    height: screenCoords.height / pixelScale,
    page: screenCoords.page
  };
}

/**
 * Apply zoom scaling to coordinates
 *
 * @param coords - Coordinates to scale
 * @param zoomLevel - Zoom multiplier (1.0 = 100%, 1.5 = 150%)
 * @returns Scaled coordinates
 *
 * @example
 * const zoomedCoords = applyZoom(baseCoords, 1.5); // 150% zoom
 */
export function applyZoom(coords: Coordinates, zoomLevel: number): Coordinates {
  return {
    x: coords.x * zoomLevel,
    y: coords.y * zoomLevel,
    width: coords.width * zoomLevel,
    height: coords.height * zoomLevel,
    page: coords.page
  };
}

/**
 * Remove zoom scaling from coordinates
 *
 * @param coords - Zoomed coordinates
 * @param zoomLevel - Current zoom level
 * @returns Unzoomed coordinates
 */
export function removeZoom(coords: Coordinates, zoomLevel: number): Coordinates {
  if (zoomLevel === 0) return coords; // Avoid division by zero

  return {
    x: coords.x / zoomLevel,
    y: coords.y / zoomLevel,
    width: coords.width / zoomLevel,
    height: coords.height / zoomLevel,
    page: coords.page
  };
}

/**
 * Calculate page offset for multi-page documents
 *
 * @param pageNumber - Page number (1-indexed)
 * @param pageHeight - Height of each page in pixels
 * @param pageGap - Gap between pages in pixels
 * @returns Vertical offset for the page
 */
export function calculatePageOffset(
  pageNumber: number,
  pageHeight: number,
  pageGap: number = 20
): number {
  if (pageNumber <= 1) return 0;
  return (pageNumber - 1) * (pageHeight + pageGap);
}

/**
 * Merge multiple bounding boxes into one
 * Useful for multi-word fields
 *
 * @param boxes - Array of bounding boxes
 * @returns Single merged bounding box
 *
 * @example
 * const merged = mergeBoundingBoxes([
 *   { x: 100, y: 200, width: 50, height: 20, page: 1 },
 *   { x: 155, y: 200, width: 50, height: 20, page: 1 }
 * ]);
 * // Result: { x: 100, y: 200, width: 105, height: 20, page: 1 }
 */
export function mergeBoundingBoxes(boxes: Coordinates[]): Coordinates | null {
  if (!boxes || boxes.length === 0) return null;
  if (boxes.length === 1) return boxes[0];

  const page = boxes[0].page;
  const minX = Math.min(...boxes.map(b => b.x));
  const minY = Math.min(...boxes.map(b => b.y));
  const maxX = Math.max(...boxes.map(b => b.x + b.width));
  const maxY = Math.max(...boxes.map(b => b.y + b.height));

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    page
  };
}

/**
 * Check if coordinates are valid (non-zero and positive)
 *
 * @param coords - Coordinates to validate
 * @returns true if valid, false otherwise
 */
export function isValidCoordinates(coords: Coordinates): boolean {
  return (
    coords &&
    coords.x >= 0 &&
    coords.y >= 0 &&
    coords.width > 0 &&
    coords.height > 0 &&
    coords.page > 0
  );
}

/**
 * Normalize coordinates to ensure they're within page bounds
 *
 * @param coords - Coordinates to normalize
 * @param pdfDimensions - PDF page dimensions
 * @returns Normalized coordinates
 */
export function normalizeCoordinates(
  coords: Coordinates,
  pdfDimensions: PDFDimensions
): Coordinates {
  const { pageWidth, pageHeight } = pdfDimensions;

  return {
    x: Math.max(0, Math.min(coords.x, pageWidth - coords.width)),
    y: Math.max(0, Math.min(coords.y, pageHeight - coords.height)),
    width: Math.max(1, Math.min(coords.width, pageWidth)),
    height: Math.max(1, Math.min(coords.height, pageHeight)),
    page: coords.page
  };
}

/**
 * Get default PDF dimensions for a standard A4 page
 *
 * @returns PDFDimensions with A4 defaults
 */
export function getDefaultPDFDimensions(): PDFDimensions {
  return {
    pageWidth: PDF_PAGE_SIZES.A4.width,
    pageHeight: PDF_PAGE_SIZES.A4.height,
    scale: 1.0,
    viewportWidth: 0,
    viewportHeight: 0
  };
}
